import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  Phone,
  Car,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Search,
  FileText,
  Eye,
  Shield,
  MapPin,
  Star,
  AlertTriangle,
  Calendar,
  WifiOff,
} from "lucide-react";

export const CityAdminDriversManagement = ({ cityName }) => {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchDrivers();
  }, [user]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/city-admin/${user.id}/all-drivers`);
      setDrivers(res.data.drivers || []);
    } catch (err) {
      toast.error("Failed to load drivers");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (driverId, approved) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/city-admin/${user.id}/drivers/${driverId}/approve?approved=${approved}`);
      toast.success(`Driver ${approved ? 'approved' : 'rejected'} successfully`);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update driver");
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async (driverId, isActive) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/city-admin/${user.id}/drivers/${driverId}/status?is_active=${isActive}`);
      toast.success(`Driver ${isActive ? 'enabled' : 'disabled'} successfully`);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update driver status");
    } finally {
      setProcessing(false);
    }
  };

  const handleAssignToCity = async (driverId) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/city-admin/${user.id}/drivers/${driverId}/assign-city`);
      toast.success("Driver assigned to your city successfully");
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign driver");
    } finally {
      setProcessing(false);
    }
  };

  const makeDriverOffline = async (driverId, driverName) => {
    try {
      await axios.put(`${API}/driver/${driverId}/status`, {
        is_online: false
      });
      toast.success(`${driverName || 'Driver'} is now offline`);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update driver status");
    }
  };

  const openDocuments = async (driver) => {
    setSelectedDriver(driver);
    setShowDocDialog(true);
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API}/city-admin/${user.id}/drivers/${driver.id}/documents`);
      setDocuments(res.data || []);
    } catch (err) {
      toast.error("Failed to load documents");
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleVerifyDocument = async (docType, verified, notes = null) => {
    setProcessing(true);
    try {
      await axios.put(
        `${API}/city-admin/${user.id}/drivers/${selectedDriver.id}/documents/${docType}/verify?verified=${verified}`,
        { notes }
      );
      toast.success(`Document ${verified ? 'verified' : 'rejected'}`);
      // Refresh documents
      const res = await axios.get(`${API}/city-admin/${user.id}/drivers/${selectedDriver.id}/documents`);
      setDocuments(res.data || []);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to verify document");
    } finally {
      setProcessing(false);
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.name?.toLowerCase().includes(search.toLowerCase()) ||
      driver.phone?.includes(search);
    
    const matchesFilter = 
      filter === "all" ||
      (filter === "pending" && driver.approval_status === "pending") ||
      (filter === "approved" && driver.approval_status === "approved") ||
      (filter === "rejected" && driver.approval_status === "rejected") ||
      (filter === "online" && driver.is_online) ||
      (filter === "unassigned" && !driver.assigned_city_id);
    
    let matchesDate = true;
    if (dateFilter !== "all" && driver.created_at) {
      const createdAt = new Date(driver.created_at);
      const now = new Date();
      if (dateFilter === "today") {
        matchesDate = createdAt.toDateString() === now.toDateString();
      } else if (dateFilter === "week") {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        matchesDate = createdAt >= weekAgo;
      } else if (dateFilter === "month") {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        matchesDate = createdAt >= monthAgo;
      }
    }
    
    return matchesSearch && matchesFilter && matchesDate;
  });

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      rejected: "bg-red-100 text-red-700",
      disabled: "bg-gray-100 text-gray-600"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status || "pending"}
      </span>
    );
  };

  const getDocStatusBadge = (status) => {
    switch (status) {
      case "verified":
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Verified</span>;
      case "rejected":
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">Pending</span>;
    }
  };

  const stats = {
    total: drivers.length,
    approved: drivers.filter(d => d.approval_status === "approved").length,
    pending: drivers.filter(d => d.approval_status === "pending").length,
    online: drivers.filter(d => d.is_online).length,
    unassigned: drivers.filter(d => !d.assigned_city_id).length
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Driver Management</h1>
          <p className="text-muted-foreground">Manage drivers in {cityName}</p>
        </div>
        <Button onClick={fetchDrivers} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Users className="w-4 h-4" />
            <span className="text-xs">Total Drivers</span>
          </div>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-xs">Approved</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center gap-2 text-yellow-600 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs">Pending</span>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <Car className="w-4 h-4" />
            <span className="text-xs">Online Now</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.online}</p>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <div className="flex items-center gap-2 text-orange-600 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs">Unassigned</span>
          </div>
          <p className="text-2xl font-bold text-orange-600">{stats.unassigned}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-36" data-testid="date-filter">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Joined" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
          </SelectContent>
        </Select>
        <Tabs value={filter} onValueChange={setFilter} className="w-full md:w-auto">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="online">Online</TabsTrigger>
            <TabsTrigger value="unassigned">Unassigned</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Drivers Table */}
      <div className="bg-card rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Driver</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDrivers.map((driver) => (
              <TableRow key={driver.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{driver.name}</p>
                    <p className="text-xs text-muted-foreground">{driver.total_rides || 0} rides</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm">
                    <Phone className="w-3 h-3" />
                    {driver.phone}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Car className="w-3 h-3" />
                    <span className="capitalize">{driver.vehicle_type || "Not set"}</span>
                  </div>
                  {driver.vehicle_number && (
                    <p className="text-xs text-muted-foreground">{driver.vehicle_number}</p>
                  )}
                </TableCell>
                <TableCell>
                  {driver.assigned_city_name ? (
                    <div className="flex items-center gap-1 text-sm">
                      <MapPin className="w-3 h-3 text-green-600" />
                      {driver.assigned_city_name}
                    </div>
                  ) : (
                    <span className="text-orange-600 text-sm">Not assigned</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(driver.approval_status)}
                    {driver.is_online && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        Online
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span>{driver.rating?.toFixed(1) || "5.0"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    {/* Documents button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDocuments(driver)}
                      data-testid={`docs-${driver.id}`}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Docs
                    </Button>
                    
                    {/* Assign to city if unassigned */}
                    {!driver.assigned_city_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={() => handleAssignToCity(driver.id)}
                        disabled={processing}
                        data-testid={`assign-${driver.id}`}
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        Claim
                      </Button>
                    )}
                    
                    {/* Approval actions for pending */}
                    {driver.approval_status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(driver.id, true)}
                          disabled={processing}
                          data-testid={`approve-${driver.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleApprove(driver.id, false)}
                          disabled={processing}
                          data-testid={`reject-${driver.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}
                    
                    {/* Toggle enable/disable for approved drivers */}
                    {driver.approval_status === "approved" && (
                      <Button
                        size="sm"
                        variant={driver.is_active === false ? "default" : "outline"}
                        className={driver.is_active === false ? "" : "text-orange-600 border-orange-200 hover:bg-orange-50"}
                        onClick={() => handleToggleStatus(driver.id, driver.is_active === false)}
                        disabled={processing}
                        data-testid={`toggle-${driver.id}`}
                      >
                        {driver.is_active === false ? "Enable" : "Disable"}
                      </Button>
                    )}
                    
                    {/* Make Offline button for online drivers */}
                    {driver.is_online && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        onClick={() => makeDriverOffline(driver.id, driver.name)}
                        disabled={processing}
                        data-testid={`make-offline-${driver.id}`}
                      >
                        <WifiOff className="w-4 h-4 mr-1" />
                        Offline
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredDrivers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  {loading ? "Loading drivers..." : "No drivers found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Documents Dialog */}
      <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Driver Documents - {selectedDriver?.name}
            </DialogTitle>
            <DialogDescription>
              Review and verify driver's uploaded documents
            </DialogDescription>
          </DialogHeader>
          
          {loadingDocs ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No documents uploaded yet
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {documents.map((doc) => (
                <div key={doc.doc_type} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium capitalize">{doc.doc_type?.replace(/_/g, ' ')}</h4>
                      {doc.doc_number && (
                        <p className="text-sm text-muted-foreground">No: {doc.doc_number}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getDocStatusBadge(doc.status)}
                    </div>
                  </div>
                  
                  {doc.file_url && (
                    <div className="mt-3">
                      <a 
                        href={doc.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm flex items-center gap-1 hover:underline"
                      >
                        <Eye className="w-4 h-4" />
                        View Document
                      </a>
                    </div>
                  )}
                  
                  {doc.status === "pending" && (
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleVerifyDocument(doc.doc_type, true)}
                        disabled={processing}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Verify
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleVerifyDocument(doc.doc_type, false, "Document not valid")}
                        disabled={processing}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                  
                  {doc.verification_notes && (
                    <p className="text-sm text-muted-foreground mt-2 italic">
                      Notes: {doc.verification_notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CityAdminDriversManagement;
